import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PackageDialog } from "@/components/dialogs/PackageDialog";
import { PaymentDialog } from "@/components/dialogs/PaymentDialog";
import { DataUnavailable } from "@/components/pwa/OfflineBanner";
import { useResource } from "@/pwa/useResource";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["Tutti", "Attivo", "In esaurimento", "Esaurito"];

export default function Packages() {
  const [filter, setFilter] = useState("Tutti");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [pay, setPay] = useState(null);
  const nav = useNavigate();

  const { data: pkd, unavailable, reload: load } = useResource(useCallback(async () => {
    const params = filter === "Tutti" ? {} : { status: filter };
    const r = await apiGet("/packages", { params }); return { data: r.data, stale: r.stale };
  }, [filter]), [filter]);
  const packages = pkd || [];
  const { guard } = useOnlineGuard();

  const del = async (p) => {
    try { await api.delete(`/packages/${p.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Pacchetti</h1>
        <Button data-testid="add-package-btn" onClick={guard(() => { setEdit(null); setOpen(true); })} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Nuovo pacchetto</Button>
      </div>

      <div className="flex gap-1 rounded-full border border-border bg-card p-1 w-fit flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`pkg-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{f}</button>
        ))}
      </div>

      {unavailable ? <DataUnavailable /> : (
      <div className="grid sm:grid-cols-2 gap-3" data-testid="packages-grid">
        {packages.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-2">
              <button onClick={() => nav(`/cani/${p.dog_id}`)} className="text-left">
                <div className="font-head font-bold hover:underline">{p.dog_name}</div>
                <div className="text-sm text-muted-foreground">{p.owner_name} · aperto {fmtDate(p.opening_date)}</div>
              </button>
              <div className="flex flex-col items-end gap-1">
                <StatusBadge value={p.status} />
                <StatusBadge value={p.payment_status} />
              </div>
            </div>
            <div className="mt-3">
              <Progress value={(p.used_credits / p.total_credits) * 100} className="h-2.5" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{p.used_credits}/{p.total_credits} usate</span>
                <span className="font-mono">{p.remaining_credits} residui</span>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span>Valore <b>{fmtEUR(p.total_price)}</b></span>
              <span>Incassato <b>{fmtEUR(p.paid_amount)}</b></span>
              {p.client_credit_applied > 0 && <span className="text-[#1E5E2B]">Credito cliente <b>{fmtEUR(p.client_credit_applied)}</b></span>}
              {p.outstanding > 0 && <span className="text-accent font-semibold">Residuo {fmtEUR(p.outstanding)}</span>}
            </div>
            <div className="mt-3 flex gap-2">
              {p.outstanding > 0 && <Button size="sm" data-testid={`pkg-pay-${p.id}`} onClick={() => setPay(p)} className="rounded-full"><CreditCard className="h-4 w-4 mr-1" />Incassa</Button>}
              <Button size="sm" variant="outline" data-testid={`pkg-edit-${p.id}`} onClick={() => { setEdit(p); setOpen(true); }} className="rounded-full"><Pencil className="h-4 w-4" /></Button>
              {p.used_credits === 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`pkg-del-${p.id}`} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Eliminare il pacchetto?</AlertDialogTitle><AlertDialogDescription>Solo pacchetti senza crediti usati e senza pagamenti.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(p)}>Elimina</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
        {packages.length === 0 && <p className="text-sm text-muted-foreground">Nessun pacchetto.</p>}
      </div>
      )}

      <PackageDialog open={open} onOpenChange={setOpen} pkg={edit} onSaved={load} />
      <PaymentDialog open={!!pay} onOpenChange={(v) => !v && setPay(null)} presetOwnerId={pay?.owner_id} presetDogId={pay?.dog_id} presetPackage={pay} onSaved={load} />
    </div>
  );
}
